import styled, { css } from 'styled-components';
import { vars } from '../../theme';
import { Minus8, Plus8, Minus16, Plus16 } from '@honeybook/icons';
import { darken, applyOpacity } from '../../utils/color.utils';
import HBInput from '../HBInput/HBInput';

function getContainerStyles(highlightColor: string) {
    return css`
        background-color: ${applyOpacity(
            highlightColor || vars.colors.text.primary,
            15
        )};
    `;
}

function getValueStyles(valueColor: string) {
    return css`
        color: ${valueColor || vars.colors.text.primary} !important;
    `;
}

function getControlButtonsStyles(highlightColor: string) {
    return css<{ disabled?: boolean }>`
        ${Decrease}, ${Increase}, ${DecreaseDrawer}, ${IncreaseDrawer} {
            svg path,
            svg rect,
            svg polygon {
                fill: ${highlightColor || vars.colors.text.primary};
            }
        }

        ${({ disabled }) =>
            disabled &&
            css`
                ${Decrease}, ${Increase}, ${DecreaseDrawer}, ${IncreaseDrawer} {
                    color: ${highlightColor || vars.colors.text.primary};
                    opacity: 0.5;
                }
            `};

        ${({ disabled }) =>
            !disabled &&
            css`
                &:hover {
                    cursor: pointer;
                    ${Decrease}, ${Increase}, ${DecreaseDrawer}, ${IncreaseDrawer} {
                        svg path,
                        svg rect,
                        svg polygon {
                            fill: ${darken(
                                highlightColor || vars.colors.text.primary,
                                0.2
                            )};
                        }
                    }
                }
            `};
    `;
}

export const Container = styled.div`
    background-color: ${vars.colors.surface.container};
    border-radius: ${vars.radius.sm};
`;

export const CounterContainer = styled.div<{
    highlightColor: string;
    disabled?: boolean;
    isInDrawer?: boolean;
}>`
    display: flex;
    align-items: center;
    border-radius: ${vars.radius.sm};
    width: fit-content;
    height: 26px;

    ${({ isInDrawer }) =>
        isInDrawer &&
        css`
            height: 40px;
        `};

    ${({ disabled }) =>
        disabled &&
        css`
            cursor: not-allowed;
        `};

    ${({ highlightColor }) => getContainerStyles(highlightColor)}
`;

export const Decrease = styled(Minus8)`
    svg {
        display: flex;
    }
`;

export const Increase = styled(Plus8)`
    svg {
        display: flex;
    }
`;

export const DecreaseDrawer = styled(Minus16)`
    svg {
        display: flex;
    }
`;

export const IncreaseDrawer = styled(Plus16)`
    svg {
        display: flex;
    }
`;

export const ControlButtonContainer = styled.div<{
    mode: 'increase' | 'decrease';
    disabled?: boolean;
    highlightColor: string;
    isInDrawer?: boolean;
}>`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    padding-right: ${({ mode }) => (mode === 'increase' ? '8px' : '0')};
    padding-left: ${({ mode }) => (mode === 'decrease' ? '8px' : '0')};

    ${({ isInDrawer, mode }) =>
        isInDrawer &&
        css`
            padding-right: ${mode === 'increase' ? '14px' : '0'};
            padding-left: ${mode === 'decrease' ? '14px' : '0'};
        `};

    ${({ disabled }) =>
        disabled &&
        css`
            pointer-events: none;
            opacity: 0.5;
        `}

    ${({ highlightColor }) => getControlButtonsStyles(highlightColor)};
`;

export const InputValue = styled(HBInput)<{
    $valueColor: string;
    $isInDrawer?: boolean;
    disabled?: boolean;
}>`
    width: ${({ $isInDrawer }) => ($isInDrawer ? '120px' : '32px')} !important;
    padding: 0 !important;
    text-align: center;
    background: none !important;
    border-color: transparent !important;

    ${({ disabled }) =>
        !disabled &&
        css`
            border-radius: 0px !important;
        `}

    ${({ disabled }) =>
        disabled &&
        css`
            opacity: 0.5 !important;
            pointer-events: none;
        `}
    
    ${({ $isInDrawer }) =>
        $isInDrawer &&
        css`
            font-size: 22px !important;
            height: 40px !important;
        `};

    &:hover {
        border: none;
    }

    &:focus {
        background: none !important;
    }

    ${({ $valueColor }) => getValueStyles($valueColor)};
`;
